import React, { useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, Heart, Coins, Radio, Mic2, Film, SkipForward, FileText, X, Loader2 } from "lucide-react";
import { usePlayer } from "@/lib/player";
import { Link } from "react-router-dom";
import ClipMaker from "@/components/ClipMaker";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function PersistentPlayer() {
  const { station, current, isPlaying, togglePlay, volume, setVolume, muted, setMuted, error, founderDrop, skipFounder } = usePlayer();
  const [clipOpen, setClipOpen] = useState(false);
  const [lyricsOpen, setLyricsOpen] = useState(false);
  const [lyrics, setLyrics] = useState(null); // null = not loaded, "" = none, string = text
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const trackId = current?.track_id;
  // Fetch lyrics lazily when panel opens; reset when the track changes.
  useEffect(() => { setLyrics(null); }, [trackId]);
  useEffect(() => {
    if (!lyricsOpen || !trackId || lyrics !== null) return;
    let cancelled = false;
    setLyricsLoading(true);
    api.get(`/tracks/${trackId}/lyrics`).then(r => {
      if (cancelled) return;
      setLyrics(r.data?.lyrics || "");
    }).catch(() => { if (!cancelled) setLyrics(""); })
      .finally(() => { if (!cancelled) setLyricsLoading(false); });
    return () => { cancelled = true; };
  }, [lyricsOpen, trackId, lyrics]);
  if (!station) return null;
  const isDj = current?.kind === "dj_drop";
  return (
    <>
      {founderDrop && (
        <div
          className="fixed inset-x-0 bottom-[88px] sm:bottom-[76px] z-[60] mx-auto max-w-3xl px-4"
          data-testid="founder-drop-banner"
        >
          <div className="relative overflow-hidden rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-fuchsia-600/30 via-black/80 to-cyan-500/20 backdrop-blur-xl shadow-[0_8px_32px_rgba(255,0,128,0.25)]">
            <div className="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none bg-[radial-gradient(circle_at_20%_30%,rgba(255,0,200,0.35),transparent_50%),radial-gradient(circle_at_80%_70%,rgba(0,220,255,0.35),transparent_50%)]" />
            <div className="relative flex items-center gap-3 p-3 sm:p-4">
              <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/60 flex items-center justify-center">
                <Mic2 className="w-5 h-5 text-emerald-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[10px] fl-mono tracking-[0.25em] uppercase text-emerald-300">
                  <span className="live-dot" /> On-Air · Founder Drop
                </div>
                <div className="truncate text-sm sm:text-base text-white font-semibold">
                  {founderDrop.title || "DJ Anup — Welcome to Fliboe"}
                </div>
                <div className="truncate text-[11px] text-white/60">
                  {founderDrop.subtitle || "A word from your founder"}
                </div>
              </div>
              <button
                onClick={skipFounder}
                className="fl-btn ghost !py-1.5 !px-3 text-xs"
                data-testid="founder-drop-skip"
                title="Skip to the music"
              >
                <SkipForward className="w-4 h-4 text-emerald-300" />
                <span className="hidden sm:inline">Skip</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#0A0A0A]/95 border-t border-white/10 shadow-[0_-8px_32px_rgba(0,0,0,0.6)]" data-testid="persistent-player">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        {/* Now Playing */}
        <Link to={`/s/${station.station_id}`} className="flex items-center gap-3 min-w-0 flex-1" data-testid="player-station-link">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-800 shrink-0 relative">
            {(current?.cover_url || station.cover_url) ? (
              <img src={resolveImg(current?.cover_url || station.cover_url)} alt="" className="w-full h-full object-cover object-top" />
            ) : (
              <Radio className="w-6 h-6 text-emerald-500 m-auto mt-3" />
            )}
            <div className="absolute inset-0 ring-1 ring-emerald-500/30" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] fl-mono tracking-[0.25em] text-emerald-500 uppercase">
              <span className="live-dot" /> {isDj ? "ON-AIR" : "LIVE"} · {station.name}
            </div>
            <div className="truncate text-sm text-white font-medium flex items-center gap-1.5" data-testid="player-track-title">
              {isDj && <Mic2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
              {current ? (isDj ? "Your host is on the mic" : `${current.title} — ${current.artist}`) : "Tuning…"}
            </div>
            {error && (
              <div className="truncate text-[11px] text-red-400 mt-0.5" data-testid="player-error">
                {error}
              </div>
            )}
          </div>
        </Link>

        {/* Equalizer */}
        <div className="hidden md:flex items-end h-6" aria-hidden>
          <span className="eq-bar" /><span className="eq-bar" /><span className="eq-bar" /><span className="eq-bar" />
        </div>

        {/* Play */}
        <button onClick={togglePlay} className="w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black flex items-center justify-center transition" data-testid="player-play-button" aria-label="Play/Pause">
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>

        {/* Volume */}
        <div className="hidden sm:flex items-center gap-2 w-36">
          <button onClick={() => setMuted(!muted)} className="text-slate-400 hover:text-white" data-testid="player-mute-button">
            {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            type="range" min={0} max={1} step={0.01}
            value={muted ? 0 : volume}
            onChange={(e) => { setVolume(parseFloat(e.target.value)); if (muted) setMuted(false); }}
            className="w-full accent-emerald-500"
            data-testid="player-volume-slider"
          />
        </div>

        {/* Lyrics — opens a popup with the current track's lyrics */}
        {current && !isDj && (
          <button
            onClick={() => setLyricsOpen(true)}
            className="fl-btn ghost !py-2 !px-3 text-xs"
            data-testid="player-lyrics-button"
            title="Show lyrics"
          >
            <FileText className="w-4 h-4 text-emerald-500" /> <span className="hidden lg:inline">Lyrics</span>
          </button>
        )}
        {/* Make a TikTok-style clip from the current track */}
        {current?.audio_url && (
          <button
            onClick={() => setClipOpen(true)}
            className="fl-btn ghost !py-2 !px-3 text-xs"
            data-testid="player-clip-button"
            title="Export a 9:16 clip for TikTok / Reels / Stories"
          >
            <Film className="w-4 h-4 text-emerald-500" /> <span className="hidden lg:inline">Make clip</span>
          </button>
        )}
        {/* Tip jar — defensive: if the saved value isn't a valid URL, fall back gracefully */}
        {station.tip_jar_url ? (() => {
          const raw = (station.tip_jar_url || "").trim();
          let href = raw;
          if (!/^https?:\/\//i.test(href)) {
            // Best-effort normalize on the client too, so legacy stations still work
            // until the owner re-saves their settings.
            const low = raw.toLowerCase();
            if (low.startsWith("paypal.me/") || low.startsWith("cash.app/") || low.startsWith("venmo.com/")) {
              href = "https://" + raw;
            } else {
              const handle = raw.replace(/^[@$]/, "").split(/[\s-]+/).filter(Boolean);
              const tag = (handle[handle.length - 1] || "").toLowerCase();
              const name = handle[0] || raw;
              if (tag.includes("cash")) href = `https://cash.app/$${name}`;
              else if (tag.includes("venmo")) href = `https://venmo.com/${name}`;
              else href = `https://paypal.me/${name}`;
            }
          }
          return (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="relative fl-btn ghost !py-2 !px-3 text-xs !flex-col !gap-0.5 tip-jar-pulse"
              data-testid="player-tip-button"
              title="Tip the host"
            >
              <span className="fl-mono text-[9px] tracking-[0.28em] font-bold text-emerald-400 leading-none" data-testid="player-tip-label">TIP</span>
              <Coins className="w-4 h-4 text-emerald-500" />
            </a>
          );
        })() : null}
      </div>
      <ClipMaker
        open={clipOpen}
        onClose={() => setClipOpen(false)}
        station={station}
        audioUrl={current?.audio_url}
        audioDuration={current?.duration}
        title={current?.title}
        artist={current?.artist}
        trackCoverUrl={current?.cover_url}
      />
      {/* Lyrics overlay */}
      {lyricsOpen && current && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setLyricsOpen(false)}
          data-testid="player-lyrics-overlay"
        >
          <div
            onClick={e => e.stopPropagation()}
            className="w-full max-w-xl bg-[#0A0A0A] border border-emerald-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
          >
            <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
              <div className="min-w-0">
                <div className="fl-mono text-[10px] tracking-[0.28em] uppercase text-emerald-400">// Lyrics</div>
                <div className="truncate text-sm text-white font-semibold mt-0.5">{current.title}</div>
                <div className="truncate text-[11px] text-white/50">{current.artist}</div>
              </div>
              <button
                onClick={() => setLyricsOpen(false)}
                className="text-white/60 hover:text-white p-1 rounded"
                data-testid="player-lyrics-close"
                aria-label="Close lyrics"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed" data-testid="player-lyrics-body">
              {lyricsLoading ? (
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin" /> Searching LRCLIB…
                </div>
              ) : lyrics ? (
                lyrics
              ) : (
                <div className="text-slate-500 text-xs">
                  No lyrics found for this track. Lyrics come from the free LRCLIB community database — if the song isn't in there yet, nothing will show.
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40 fl-mono tracking-widest text-right">
              lyrics via lrclib.net
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
